// PDF formal del "Reporte de Cierres y Liquidación de Efectivo" (para entregar al cliente).
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toMoney } from '../utils/numbers';
import { formatDate, formatDateTime } from '../utils/dates';

const MARCA = [31, 45, 51]; // encabezados de tabla (color del menú)
const ACENTO = [47, 111, 115]; // color principal
const OSCURO = [31, 41, 51];
const GRIS = [91, 107, 115];
const fecha = formatDate;
const fechaHora = formatDateTime;
const turno = (t) => ({ dia: 'Día', noche: 'Noche', dia_completo: 'Día completo' }[t] || '—');
const ESTADO = { Pendiente: 'Pendiente de liquidar', Parcial: 'Parcialmente liquidado', Liquidado: 'Liquidado' };

/**
 * @param {object} rep  respuesta de /liquidaciones/reporte
 * @param {object} meta { sucursal, generadoPor, estado, cajero }
 */
export function exportReporteLiquidacionPdf(rep, meta, { imprimir = false } = {}) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth(), H = pdf.internal.pageSize.getHeight(), M = 40;
  let y = M;
  const espacio = (alto) => { if (y + alto > H - 50) { pdf.addPage(); y = M; } };
  const titulo = (txt, size = 13) => { espacio(30); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(size); pdf.setTextColor(...OSCURO); pdf.text(txt, M, y); y += size + 6; };
  const tabla = (opts) => { autoTable(pdf, { startY: y, margin: { left: M, right: M }, theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, textColor: [31, 41, 51], lineColor: [217, 226, 227] },
    headStyles: { fillColor: MARCA, textColor: 255, fontStyle: 'bold' }, ...opts }); y = pdf.lastAutoTable.finalY + 14; };

  // Encabezado
  pdf.setFillColor(...ACENTO); pdf.rect(0, 0, W, 6, 'F');
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22); pdf.setTextColor(...OSCURO); pdf.text('QUE TACO', M, y + 14); y += 34;
  pdf.setFontSize(14); pdf.text('Reporte de Cierres y Liquidación de Efectivo', M, y); y += 20;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(...GRIS);
  const filtros = [`Periodo: ${fecha(rep.desde)} al ${fecha(rep.hasta)}`, `Sucursal: ${meta.sucursal}`,
    meta.estado ? `Estado de liquidación: ${ESTADO[meta.estado] || meta.estado}` : null, meta.cajero ? `Cajero: ${meta.cajero}` : null].filter(Boolean);
  filtros.forEach((t) => { pdf.text(t, M, y); y += 13; });
  pdf.text(`Generado: ${formatDateTime(new Date())}${meta.generadoPor ? ` por ${meta.generadoPor}` : ''} · ${rep.resumen.cierres} cierre(s)`, M, y); y += 20;

  // Resumen general
  const r = rep.resumen;
  titulo(rep.desde === rep.hasta ? 'Resumen general' : 'Resumen consolidado del periodo');
  const fila = (concepto, monto, opts = {}) => [{ content: concepto, styles: opts }, { content: toMoney(monto), styles: { halign: 'right', ...opts } }];
  const grupo = (txt) => [{ content: txt, colSpan: 2, styles: { fillColor: [231, 240, 240], fontStyle: 'bold', textColor: OSCURO } }];
  tabla({ head: [['Concepto', { content: 'Monto', styles: { halign: 'right' } }]], columnStyles: { 1: { cellWidth: 130 } }, body: [
    grupo('Ventas'), fila('Ventas totales', r.ventasTotales, { fontStyle: 'bold' }), fila('Efectivo', r.ventasEfectivo), fila('Tarjeta', r.ventasTarjeta), fila('PedidosYa', r.ventasPedidosYa),
    grupo('Operación del turno'), fila('Compras', r.compras), fila('Gastos', r.gastos),
    grupo('Efectivo y liquidación'), fila('Efectivo disponible para liquidar', r.efectivoGenerado, { fontStyle: 'bold' }),
    fila('Efectivo depositado', r.depositado), fila('Entrega de efectivo', r.entregadoDuena), fila('Pagado a proveedores', r.pagadoProveedores),
    fila('Otros destinos', r.otrosDestinos), fila('Reservado para aperturas', r.reservado), fila('Pendiente de liquidar', r.pendienteLiquidar, { fontStyle: 'bold' }),
    grupo('Cuadre de caja'), fila('Faltantes', r.faltantes), fila('Sobrantes', r.sobrantes),
  ] });

  // Detalle: agrupado por día y sucursal
  if (rep.cierres.length) titulo(rep.desde === rep.hasta ? 'Detalle por cierre' : 'Detalle por día y sucursal');
  let diaActual = null;
  rep.cierres.forEach((c) => {
    if (rep.desde !== rep.hasta && c.fecha !== diaActual) {
      diaActual = c.fecha; espacio(40);
      pdf.setFillColor(238, 245, 241); pdf.rect(M, y - 11, W - 2 * M, 18, 'F');
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(...OSCURO); pdf.text(`Día ${fecha(c.fecha)}`, M + 6, y + 2); y += 20;
    }
    espacio(150);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(...OSCURO);
    pdf.text(`Cierre ${fecha(c.fecha)} – Turno ${turno(c.turnoId)} · ${c.sucursal}`, M, y); y += 13;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(...GRIS);
    pdf.text(`Cajero: ${c.cajero}   ·   Cierre: ${fechaHora(c.cierreAt)}   ·   Ventas: ${c.ventas.fuente === 'sistema' ? 'sistema' : c.ventas.fuente === 'reportado' ? 'reportadas' : 'pendientes'}`, M, y); y += 8;
    const dif = c.efectivoReportado == null ? c.diferenciaVentas == null ? 'Pendiente de datos del sistema' : `Ventas ${toMoney(c.diferenciaVentas)}`
      : c.diferencia == null ? 'Pendiente de revisión' : `${c.diferencia < 0 ? 'Faltante' : c.diferencia > 0 ? 'Sobrante' : 'Cuadrado'} ${toMoney(Math.abs(c.diferencia))}`;
    tabla({ body: [
      ['Ventas efectivo', c.ventas.efectivo == null ? 'Pendiente' : toMoney(c.ventas.efectivo), 'Compras', toMoney(c.compras)],
      ['Ventas tarjeta', toMoney(c.ventas.tarjeta), 'Gastos', toMoney(c.gastos)],
      ['Ventas PedidosYa', toMoney(c.ventas.pedidosYa), 'Efectivo esperado', c.efectivoEsperado == null ? 'Pendiente' : toMoney(c.efectivoEsperado)],
      ['', '', c.efectivoReportado == null ? 'Sin conteo físico' : 'Efectivo contado', c.efectivoReportado == null ? '—' : toMoney(c.efectivoReportado)],
      ['', '', c.efectivoReportado == null ? 'Diferencia de ventas' : 'Diferencia de caja', dif],
    ], theme: 'plain', styles: { fontSize: 9, cellPadding: 2.5 }, columnStyles: { 1: { halign: 'right' }, 3: { halign: 'right' }, 0: { textColor: GRIS }, 2: { textColor: GRIS } } });
    y -= 6;
    const L = c.liquidacion;
    tabla({ head: [['Fecha', 'Destino', 'Detalle', { content: 'Monto', styles: { halign: 'right' } }]],
      body: L.movimientos.length ? L.movimientos.map((m) => [fecha(m.fechaAt), m.tipoNombre, m.detalle, { content: toMoney(m.monto), styles: { halign: 'right' } }])
        : [[{ content: 'Sin movimientos de liquidación registrados', colSpan: 4, styles: { textColor: GRIS, fontStyle: 'italic' } }]],
      foot: [[{ content: `Total aplicado ${toMoney(L.aplicado)}   ·   Pendiente ${toMoney(L.pendiente)}   ·   Estado: ${ESTADO[L.estado] || L.estado}`, colSpan: 4, styles: { halign: 'right' } }]],
      footStyles: { fillColor: [231, 240, 240], textColor: OSCURO, fontStyle: 'bold' }, columnStyles: { 0: { cellWidth: 62 }, 1: { cellWidth: 120 }, 3: { cellWidth: 80 } } });
  });

  // Pie de página
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(...GRIS);
    pdf.text('Que Taco · Reporte de Cierres y Liquidación de Efectivo', M, H - 24);
    pdf.text(`Página ${i} de ${total}`, W - M, H - 24, { align: 'right' });
  }
  if (imprimir) { pdf.autoPrint(); if (!window.open(pdf.output('bloburl'), '_blank')) pdf.save('Reporte_Cierres_Liquidacion.pdf'); return; }
  pdf.save(`Reporte_Cierres_Liquidacion_${rep.desde}${rep.desde !== rep.hasta ? `_al_${rep.hasta}` : ''}.pdf`);
}
